@if (shouldShowFab()) {
  <button
    type="button"
    class="ai-assistant-fab"
    [class.ai-assistant-fab--hidden]="isOpen()"
    (click)="setOpen(true)"
    [attr.aria-label]="t('open-assistant')"
    *transloco="let t; prefix: 'ai-assistant'"
  >
    <img width="40" height="40" src="../assets/spotlight_white.svg" [attr.alt]="t('name')" />
  </button>
}

@if (isDrawerVisible()) {
  @if (!isPinned() && !isFullscreen() && !isMobile()) {
    <div class="ai-assistant-backdrop" (click)="setOpen(false)"></div>
  }
  <aside
    class="ai-assistant-drawer"
    [class.ai-assistant-drawer--fullscreen]="isFullscreen() || isMobile()"
    [class.ai-assistant-drawer--open]="isOpen()"
    [class.ai-assistant-drawer--pinned]="isPinned() && !isFullscreen() && !isMobile()"
    [class.ai-assistant-drawer--dragging]="isDraggingPinned()"
    [class.ai-assistant-drawer--resizing]="isResizingDrawer()"
    [style.transform]="drawerTransform()"
    [style.width]="drawerWidthStyle()"
    *transloco="let t; prefix: 'ai-assistant'"
  >
    @if (!isFullscreen() && !isMobile()) {
      <button
        type="button"
        class="ai-assistant-drawer__resize-handle"
        (pointerdown)="onDrawerResizeStart($event)"
        [attr.aria-label]="t('resize')"
        tabindex="-1"
      >
        <span class="ai-assistant-drawer__resize-grip" aria-hidden="true"></span>
      </button>
    }
    <header class="ai-assistant-drawer__header" (pointerdown)="onPinnedDragStart($event)">
      <div class="ai-assistant-drawer__title-row">
        <div class="ai-assistant-drawer__title-left">
          @if (mode() === 'ask' && selectedDataSource()) {
            <xi-icon-button
              icon="history"
              iconSize="large"
              color="CX"
              (onClick)="toggleHistoryPanel()"
              [attr.aria-label]="t('chat-history')"
            ></xi-icon-button>
          }
          <h2 class="ai-assistant-drawer__title">{{ t('name') }}</h2>
        </div>
        <div class="ai-assistant-drawer__header-actions">
          @if (!isMobile()) {
            <xi-icon-button
              [icon]="isFullscreen() ? 'exit' : 'full_screen'"
              iconSize="large"
              color="CX"
              (onClick)="toggleFullscreen()"
              [attr.aria-label]="isFullscreen() ? t('exit-fullscreen') : t('enter-fullscreen')"
            ></xi-icon-button>
            <xi-icon-button
              [icon]="isPinned() ? 'pin_filled' : 'pin'"
              iconSize="large"
              color="CX"
              [tooltip]="{ text: isPinned() ? t('unpin') : t('pin') }"
              (onClick)="togglePinned()"
              [attr.aria-label]="isPinned() ? t('unpin') : t('pin')"
            ></xi-icon-button>
            @if (isPinned()) {
              <xi-icon
                icon="drag_handle"
                iconSize="medium"
                class="ai-assistant-drawer__drag-handle"
                aria-hidden="true"
              ></xi-icon>
            }
          }
          <xi-icon-button
            icon="plus"
            iconSize="large"
            color="CX"
            (onClick)="newChat()"
            [attr.aria-label]="t('new-chat')"
          ></xi-icon-button>
          @if (isMobile()) {
            <button
              type="button"
              class="ai-assistant-drawer__mobile-menu-btn"
              (click)="toggleMobileMenu()"
              [attr.aria-label]="showMobileMenu() ? t('mobile-menu-close') : t('mobile-menu')"
            >
              <mat-icon>{{ showMobileMenu() ? 'close' : 'menu' }}</mat-icon>
            </button>
          } @else {
            <xi-icon-button
              icon="cancel"
              iconSize="large"
              color="CX"
              (onClick)="setOpen(false)"
              [attr.aria-label]="t('close')"
            ></xi-icon-button>
          }
        </div>
      </div>

      @if (isEditAvailable()) {
        <div class="ai-assistant-drawer__mode-toggle">
          <button
            type="button"
            class="ai-assistant-drawer__mode-btn"
            [class.ai-assistant-drawer__mode-btn--active]="mode() === 'ask'"
            (click)="setMode('ask')"
          >
            <xi-icon icon="search" iconSize="small"></xi-icon>
            {{ t('mode-ask') }}
          </button>
          <button
            type="button"
            class="ai-assistant-drawer__mode-btn"
            [class.ai-assistant-drawer__mode-btn--active]="mode() === 'edit'"
            (click)="setMode('edit')"
          >
            <xi-icon icon="edit" iconSize="small"></xi-icon>
            {{ t('mode-edit') }}
          </button>
        </div>
      }

      @if (mode() === 'ask' && selectedDataSource()) {
        <div class="ai-assistant-drawer__data-source-badge">
          <span class="ai-assistant-drawer__data-source-label">{{
            selectedDataSource()?.name
          }}</span>
          <xi-icon-button
            icon="cancel"
            iconSize="small"
            color="orca"
            (onClick)="clearDataSource()"
            [attr.aria-label]="t('change-data-source')"
          ></xi-icon-button>
        </div>
      }
    </header>

    <!-- Mobile Menu Overlay -->
    @if (showMobileMenu() && isMobile()) {
      <app-spotlight-assistant-mobile-menu
        [isOpen]="showMobileMenu()"
        [groups]="mobileGroups()"
        [selectedGroup]="mobileSelectedGroup()"
        [projects]="mobileProjects()"
        [selectedProject]="mobileSelectedProject()"
        [projectsLoading]="mobileProjectsLoading()"
        (groupSelected)="selectMobileGroup($event)"
        (projectSelected)="selectMobileProject($event)"
        (logoutRequested)="mobileLogout()"
      ></app-spotlight-assistant-mobile-menu>
    }

    <!-- Chat History Panel (slides from left) -->
    @if (showHistoryPanel() && mode() === 'ask') {
      <app-spotlight-assistant-chat-history-panel
        [sessions]="chatSessions()"
        [currentSessionId]="currentSessionId()"
        [projectId]="projectId"
        [collectionId]="selectedDataSource()?.collectionId ?? null"
        [filterByDataSource]="filterByDataSource()"
        (panelClosed)="onHistoryPanelClosed()"
        (messagesLoaded)="onHistoryMessagesLoaded($event)"
        (currentSessionChanged)="onHistoryCurrentSessionChanged($event)"
        (sessionsUpdated)="onHistorySessionsUpdated($event)"
        (currentSessionDeleted)="onHistoryCurrentSessionDeleted()"
        (filterByDataSourceToggled)="toggleFilterByDataSource()"
        (dataSourceSwitched)="onHistoryDataSourceSwitch($event)"
      ></app-spotlight-assistant-chat-history-panel>
    }

    <div class="ai-assistant-drawer__body">
      @if (showDataSourceSelector() && mode() === 'ask') {
        <div class="ai-assistant-drawer__data-source-selector">
          <div class="ai-assistant-drawer__data-source-header">
            <p class="ai-assistant-drawer__data-source-prompt">{{ t('select-collection') }}</p>
            @if (previousDataSource()) {
              <xi-icon-button
                icon="cancel"
                iconSize="small"
                color="orca"
                (onClick)="cancelDataSourceSelection()"
                [attr.aria-label]="t('switch-collection-cancel')"
              ></xi-icon-button>
            }
          </div>
          @for (source of dataSources(); track source.id) {
            <button
              type="button"
              class="ai-assistant-drawer__data-source-item"
              [class.ai-assistant-drawer__data-source-item--disabled]="source.isDisabled"
              [disabled]="source.isDisabled"
              [title]="source.incompatibleReason ?? ''"
              (click)="selectDataSource(source)"
            >
              <xi-icon icon="database" iconSize="small"></xi-icon>
              {{ source.name }}
              @if (source.isDisabled) {
                <span class="ai-assistant-drawer__data-source-status"
                  >({{ t('incompatible') }})</span
                >
              }
            </button>
          }
          @if (dataSources().length === 0) {
            <p class="ai-assistant-drawer__no-data-sources">{{ t('no-collections') }}</p>
          }
        </div>
      } @else {
        <app-spotlight-assistant-message-list
          [messages]="visibleMessages()"
          [isWaitingForResponse]="isWaitingForResponse()"
          (actionPromptRequested)="sendActionPrompt($event)"
        ></app-spotlight-assistant-message-list>

        <!-- Prompt Suggestions (show when no messages yet) -->
        @if (
          mode() === 'ask' &&
          selectedDataSource() &&
          visibleMessages().length <= 1 &&
          !isTyping() &&
          researchIdeas().length > 0
        ) {
          <div class="ai-assistant-drawer__suggestions">
            <p class="ai-assistant-drawer__suggestions-title">{{ t('suggestions-title') }}</p>
            <div class="ai-assistant-drawer__suggestions-grid">
              @for (idea of researchIdeas(); track $index) {
                <button
                  type="button"
                  class="ai-assistant-drawer__suggestion-card"
                  (click)="quickStart(idea)"
                >
                  <div class="ai-assistant-drawer__suggestion-icon">{{ idea.emojiIcon }}</div>
                  <div class="ai-assistant-drawer__suggestion-content">
                    <span class="ai-assistant-drawer__suggestion-title">{{ idea.title }}</span>
                    <span class="ai-assistant-drawer__suggestion-desc">{{ idea.description }}</span>
                  </div>
                </button>
              }
            </div>
          </div>
        }

        @if (isLoadingIdeas() && visibleMessages().length <= 1) {
          <div class="ai-assistant-drawer__loading-ideas">
            <div class="ai-assistant-drawer__loading-spinner"></div>
            <span>{{ t('loading-suggestions') }}</span>
          </div>
        }
      }
    </div>

    <form class="ai-assistant-drawer__footer" [formGroup]="fg">
      <textarea
        class="ai-assistant-drawer__textarea"
        formControlName="input"
        [placeholder]="mode() === 'ask' ? t('ask-placeholder') : t('edit-placeholder')"
        [readOnly]="isTyping()"
        rows="2"
        (keydown)="onPromptKeydown($event)"
      ></textarea>
      <xi-icon-button
        class="ai-assistant-drawer__send-button"
        [class.ai-assistant-drawer__send-button--disabled]="!canSend()"
        icon="arrow_right"
        color="CX"
        fill="fill"
        (onClick)="send()"
        [disabled]="!canSend()"
        [attr.aria-label]="t('send')"
        [attr.aria-disabled]="!canSend()"
      ></xi-icon-button>
    </form>
  </aside>
}
